import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './../../Styles/leadstyles/AssignLeads.css';
import { useToast } from '../ToastContext';

const apiUrl = process.env.REACT_APP_API_URL;

const AssignLeads = ({ managerId }) => {
  const [leads, setLeads] = useState([]); // State to store leads
  const [projects, setProjects] = useState([]); // State to store projects
  const [selectedLead, setSelectedLead] = useState(''); // State for selected lead
  const [selectedProject, setSelectedProject] = useState(''); // State for selected project
  const showToast = useToast();

  const effectiveManagerId = managerId || localStorage.getItem('employeeId') || '';

  // Fetch leads and projects when component mounts or managerId changes
  useEffect(() => {
    fetchLeads();
    fetchProjects();
  }, [effectiveManagerId]);

  // Function to fetch leads
  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/view-leads`, {
        params: { managerId: effectiveManagerId },
      });
      setLeads(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching leads:', error.response ? error.response.data : error.message);
      showToast('Failed to load leads. Please try again.', 'error');
    }
  };

  // Function to fetch projects
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/get-projects/${effectiveManagerId}`);
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching projects:', error.response ? error.response.data : error.message);
      showToast('Failed to load projects. Please try again.', 'error');
    }
  };

  // Function to fetch lead ID based on employee number
  const getLeadId = async (employeeNumber) => {
    try {
      const response = await axios.get(`${apiUrl}/api/get-lead-id/${employeeNumber}`);
      return response.data.lead_id; // Return the lead ID
    } catch (error) {
      console.error('Error fetching lead ID:', error.response ? error.response.data : error.message);
      showToast('Failed to fetch lead ID. Please try again.', 'error');
      return null; // Return null if there's an error
    }
  };

  // Function to check if a lead is already assigned to a project
  const checkLeadAssignment = async (leadId, projectId) => {
    try {
      const response = await axios.get(`${apiUrl}/api/check-lead-assignment`, {
        params: { lead_id: leadId, project_id: projectId },
      });
      return response.data.assigned; // Assuming the response returns { assigned: true/false }
    } catch (error) {
      console.error('Error checking lead assignment:', error.response ? error.response.data : error.message);
      showToast('Error checking assignment status. Please try again.', 'error');
      return false; // Assume not assigned if there's an error
    }
  };

  const handleAssignLead = async () => {
    if (!selectedLead || !selectedProject) {
      showToast('Please select both a lead and a project', 'error');
      return;
    }

    console.log('Selected Lead ID:', selectedLead);
    console.log('Selected Project ID:', selectedProject);

    // Fetch lead_id using the selected lead's employee_number
    const leadId = await getLeadId(selectedLead);
    
    if (!leadId) {
      // If leadId is null, it means there was an error fetching it
      return; 
    }

    // Check if the lead is already assigned to the selected project
    // NOTE: selectedProject is a MongoDB ObjectId string — do NOT parseInt() it
    const isAssigned = await checkLeadAssignment(leadId, selectedProject);
    
    if (isAssigned) {
      showToast('Lead is already assigned to this project.', 'error');
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/api/assign-lead`, {
        lead_id: leadId, // MongoDB ObjectId string
        project_id: selectedProject, // MongoDB ObjectId string — no parseInt
      });

      // Handle response based on the success or error
      if (response.data.success) {
        showToast('Lead assigned to project successfully!', 'success');
      } else {
        showToast('Unexpected error occurred. Please try again.', 'error');
      }

      // Reset selected values
      setSelectedLead('');
      setSelectedProject('');
      fetchLeads(); // Refresh leads
    } catch (error) {
      console.error('Error assigning lead to project:', error.response ? error.response.data : error.message);
      showToast('Error assigning lead to project. Please try again.', 'error');
    }
  };

  return (
    <div className="assign-leads">
      <h3>Assign Leads to Projects</h3>
      
      <div>
        <label htmlFor="lead-select">Select Lead:</label>
        <select
          id="lead-select"
          value={selectedLead}
          onChange={(e) => setSelectedLead(e.target.value)} // This should be the ID
        >
          <option value="">Select a lead</option>
          {leads.map((lead) => (
            <option key={lead._id || lead.employee_number} value={lead.employee_number}>
              {lead.full_name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="project-select">Select Project:</label>
        <select
          id="project-select"
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)} // This should be the project ID
        >
          <option value="">Select a project</option>
          {projects.map((project) => (
            <option key={project._id} value={project._id}>
              {project.project_name}
            </option>
          ))}
        </select>
      </div>

      <button onClick={handleAssignLead}>Assign Lead</button>
    </div>
  );
};

export default AssignLeads;
